import classNames from "classnames";
import React from "react";
import SecondaryButton from "web/react/components/buttons/secondary-button/secondary-button";
import LeadLink from "web/react/components/lead-link/lead-link";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import { useProductClick } from "web/react/hooks/use-product-click/use-product-click";
import { gettext } from "web/script/modules/django-i18n";
import styles from "./related-products-sidebar.css";

interface RelatedProductImageCardProps {
    product: ProductImageCard;
}

export interface RelatedProductsSidebarInterface {
    product_image_cards: ProductImageCard[];
    view_more_url: string;
}

export interface RelatedProductsSidebarProps {
    className?: string;
    relatedProductsSidebar?: RelatedProductsSidebarInterface | null;
    variant?: "vertical" | "horizontal";
}

function RelatedProductImageCard({ product }: RelatedProductImageCardProps): React.ReactElement {
    const { onClick } = useProductClick({
        reason: product.context_type,
        productSlug: product.product_slug,
        isLeadLink: !!product.lead_url,
        href: product.lead_url || product.url,
        openInNewTab: !!product.lead_url,
    });

    function beforeOnClick(callback, event): void {
        onClick(event);
        callback(event);
    }

    return (
        <div className={styles.product}>
            {product.lead_url ? (
                <LeadLink
                    className={styles.cardLink}
                    href={product.lead_url}
                    reason={product.context_type}
                    productId={String(product.id)}
                    beforeOnClick={beforeOnClick}
                    rel="nofollow"
                    openInNewTab
                >
                    <img
                        className={styles.cardImage}
                        src={product.image_url}
                        height={product.image_height}
                        width={product.image_width}
                        alt={product.image_alt_text}
                    />
                </LeadLink>
            ) : (
                <a className={styles.cardLink} href={product.url} onClick={onClick}>
                    <img
                        className={styles.cardImage}
                        src={product.image_url}
                        height={product.image_height}
                        width={product.image_width}
                        alt={product.image_alt_text}
                    />
                </a>
            )}
        </div>
    );
}

function RelatedProductsSidebar({
    className,
    relatedProductsSidebar,
    variant = "vertical",
}: RelatedProductsSidebarProps): React.ReactElement | null {
    const clickSeeMore = useClickTracker("navigation", "see_more_related", "sidebar", true);

    if (!relatedProductsSidebar) {
        return null;
    }

    return (
        <div className={className}>
            <div
                className={classNames({
                    /* eslint-disable-next-line css-modules/no-undef-class */
                    [styles.sidebar]: variant === "vertical",
                    [styles.horizontal]: variant === "horizontal",
                })}
            >
                <h5 className={styles.title}>{gettext("in_stock_product.related")}</h5>
                <div className={styles.inner}>
                    {relatedProductsSidebar.product_image_cards.map((product) => (
                        <RelatedProductImageCard key={product.id} product={product} />
                    ))}
                    {variant === "vertical" && (
                        <div className="text-center">
                            <SecondaryButton
                                small
                                href={relatedProductsSidebar.view_more_url}
                                onClick={clickSeeMore}
                                fullWidth
                            >
                                {gettext("in_stock_product.see_more_related_btn_label")}
                            </SecondaryButton>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RelatedProductsSidebar;
